import { useState } from 'react';
import { Button, ButtonProps, Typography } from '@mui/material';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import { useSetAtom } from 'jotai';
import * as kpiState from '../state';
import { useHavePermission } from '@/hooks';
import { Permissions } from '@/types';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import {
  LogTemplateType,
  useAllWorklogsTemplatesForKpiTrackerQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { GenericDialog } from '@/components/shared/GenericDialog/GenericDialog';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { titleStyles } from '../styles';

type Props = {
  selectedWorkpackId: string | undefined;
} & ButtonProps;

export const LogWorkButton = ({ selectedWorkpackId, ...props }: Props) => {
  const setKpiPanelOpen = useSetAtom(kpiState.kpiPanelOpen);
  const setWorkpackIdForKpiTracker = useSetAtom(kpiState.selectedWorkpackIdForKpiTracker);
  const setSelectedLogTemplateType = useSetAtom(kpiState.selectedLogTemplateType);
  const canAccessKpiTracker = useHavePermission(Permissions.UPDATE_KPI_LOGS);
  const structureId = useGetStructureId();
  const [showNoSectionsDialog, setShowNoSectionsDialog] = useState(false);

  const { data: templateData, loading: templateLoading } =
    useAllWorklogsTemplatesForKpiTrackerQuery({
      variables: {
        input: {
          structureId,
          type: LogTemplateType.WorklogTemplate,
        },
      },
      fetchPolicy: 'cache-first',
      skip: !structureId,
    });

  const isLoading = templateLoading;

  const template = templateData?.allLogsTemplates?.at(0);
  const sections = template?.sections;

  if (!canAccessKpiTracker) {
    return <></>;
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<QueryBuilderIcon />}
        endIcon={<AbyssIcon iconName="beta" size="md" />}
        {...props}
        disabled={props.disabled || isLoading}
        onClick={(event) => {
          event.stopPropagation();
          if (!selectedWorkpackId) return;
          if (!template || !sections || sections.length === 0) {
            setShowNoSectionsDialog(true);
            return;
          }
          setWorkpackIdForKpiTracker(selectedWorkpackId);
          setSelectedLogTemplateType(LogTemplateType.WorklogTemplate);
          setKpiPanelOpen(true);
        }}
      >
        Log Work
      </Button>

      <GenericDialog
        open={showNoSectionsDialog}
        onClose={() => setShowNoSectionsDialog(false)}
        title="Worklog feature is not enabled"
        titleStyles={titleStyles}
        actionsStyles={{ mr: 3, pb: 3 }}
        content={
          <Typography fontWeight={400} fontSize="1.4rem" color={abyssColors.primary[500]}>
            The Worklog feature is currently not enabled for this deployment. Please contact support
            to get it activated.
          </Typography>
        }
        actions={
          <Button
            variant="contained"
            onClick={() => setShowNoSectionsDialog(false)}
            sx={containedButtonStyle}
          >
            Got it
          </Button>
        }
        maxWidth="xs"
      />
    </>
  );
};
